import { createApi } from "@reduxjs/toolkit/query/react";
import { createBaseApiWithAuth } from "@/utilities/reduxToolkit";

export interface InboxTag {
  id: string;
  name: string;
  color?: string;
  createdAt: string;
  updatedAt: string;
}

export interface InboxTemplate {
  id: string;
  name: string;
  status: "UPLOADING" | "PROCESSING" | "NEEDS_REVIEW" | "APPROVED" | "FILED" | "FAILED";
  tags: InboxTag[];
  folderName?: string;
  updatedAt: string;
  createdAt: string;
  processingStage?: string | null;
  processingError?: string | null;
  fieldStats?: { total: number; confirmed: number; pending: number };
}

export interface InboxResponse {
  data: {
    templates: InboxTemplate[];
    counts: {
      review: number;
      approved: number;
      library: number;
    };
  };
}

export const inboxApi = createApi({
  ...createBaseApiWithAuth({
    reducerPath: "inboxApi",
    baseUrl: "/api",
  }),
  reducerPath: "inboxApi",
  tagTypes: ["Inbox", "InboxTags"],
  endpoints: (builder) => ({
    getInbox: builder.query<
      InboxResponse,
      {
        tab: "review" | "approved" | "in-progress";
        tagId?: string;
        sort?: "newest" | "oldest" | "name";
        search?: string;
      }
    >({
      query: (args) => {
        const params = new URLSearchParams();
        params.append("tab", args.tab);
        if (args.tagId) params.append("tagId", args.tagId);
        if (args.sort) params.append("sort", args.sort);
        if (args.search) params.append("search", args.search);
        return `/document-templates/inbox?${params.toString()}`;
      },
      keepUnusedDataFor: 0,
      providesTags: [{ type: "Inbox" }],
    }),

    getTags: builder.query<{ data: InboxTag[] }, void>({
      query: () => "/tags",
      providesTags: [{ type: "InboxTags" }],
    }),

    createTag: builder.mutation<{ data: InboxTag }, { name: string; color?: string }>({
      query: (body) => ({
        url: "/tags",
        method: "POST",
        body,
      }),
      invalidatesTags: [{ type: "InboxTags" }],
    }),

    assignTag: builder.mutation<any, { tagId: string; templateId: string }>({
      query: ({ tagId, templateId }) => ({
        url: `/tags/${tagId}/templates/${templateId}`,
        method: "POST",
      }),
      invalidatesTags: [{ type: "Inbox" }, { type: "InboxTags" }],
    }),

    removeTag: builder.mutation<any, { tagId: string; templateId: string }>({
      query: ({ tagId, templateId }) => ({
        url: `/tags/${tagId}/templates/${templateId}`,
        method: "DELETE",
      }),
      invalidatesTags: [{ type: "Inbox" }, { type: "InboxTags" }],
    }),

    bulkApprove: builder.mutation<any, { ids: string[] }>({
      query: (body) => ({
        url: "/document-templates/bulk-approve",
        method: "PATCH",
        body,
      }),
      invalidatesTags: [{ type: "Inbox" }],
    }),
  }),
});

export const {
  useGetInboxQuery,
  useGetTagsQuery,
  useCreateTagMutation,
  useAssignTagMutation,
  useRemoveTagMutation,
  useBulkApproveMutation,
} = inboxApi;
